import React, { useCallback, useState, useRef } from "react";
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector, useDispatch } from "react-redux";
import DropdownMenu from "../UI/DropdownMenu/DropdownMenu";
import DropdownMenuItem from "../UI/DropdownMenu/DropdownMenuItem";
import mobileBreakpoints from "../styles/mobileBreakpoints";
import UserChip from "../UI/UserChip/UserChip";
import useOnClickOutside from "../hooks/useOnClickOutside";
import SimpleTooltip from "../UI/SimpleTooltip/SimpleTooltip";
import { takeFullPageScreenshot } from "../utils/saveImage/saveImage";
import { useTheme } from "@emotion/react";
import NotificationTable from "../UI/Tables/NotificationTable";
import NotificationList from "../UI/Alerts/NotificationList";
import useUserAccess from "../hooks/useUserAccess";
import { inIFrame } from "../utils/browser";
import { Link } from "react-router-dom";
import { setActiveTab, setUserSelectedThemeName } from "../store/actions";
import { useShallowEqualSelector } from "../store";
import TtgFeature, { TreeIcon } from "../UI/TtgFeature";
import EditorButton from "../Pages/DataManagement/EditorButton";
import { setFullPageScreenshotMode } from "../store/actions/pageManagement";
import AISearch from "../UI/AISearch/AISearch";
import { useAIResultsModal } from "../UI/AIResultsModal/useAIResultsModal";
import GlobalSearch from "./HeaderContent/GlobalSearch";
import useHasAiAccess from "../hooks/useHasAiAccess";
import { useMustConfigure2FA } from "../hooks/authHooks";
import { css } from "../utils/ide";
import { faAdjust } from "@fortawesome/pro-solid-svg-icons";

export const topMenuHeight = 52;

const Container = styled.div(
  ({ theme }) => `
    display: flex;
    background: ${theme.background.primary};
    width: 100%;
    height: ${topMenuHeight}px;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
    position: sticky;
    top: 0;
    z-index: 1002;
    padding: 0 15px 0 0;
    margin-top: -1px;
`
);

export const processingsPage = { path: "/admin/files", name: "Upload files" };

const Actions = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: flex-end;
  margin-top: 6px;
  min-width: 0;

  @media (max-width: ${mobileBreakpoints.mobile}) {
    display: ${(props) => (props.static ? "block" : "none")};
  }
`;

const itemMargin = "0 4px";

const ActionIcon = styled.div`
  margin: ${itemMargin};
  cursor: pointer;

  path {
    fill: ${({ theme }) => theme.text.secondary};
  }
`;

const ActionIconContainer = styled.div`
  ${({ hidden }) =>
    !hidden &&
    css`
      display: flex;
    `}
  align-items: center;
  padding: 4px 12px 4px 0;
  border-right: 1px solid ${({ theme }) => theme.divider};
`;

const ThemeMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 140px;
`;

const ThemeMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const ThemeMenuItem = styled.div`
  padding: 4px 0;
  cursor: pointer;
  white-space: nowrap;
  font-size: 12px;

  ${({ active, theme }) =>
    active &&
    css`
      font-weight: 600;
      color: ${theme.general.primaryMain};
    `}
`;

const Profile = styled.div`
  margin-left: 8px;
  display: flex;
  flex-shrink: 0;
  font-size: 14px;
  justify-content: space-between;
  align-items: center;
  padding-left: 6px;
  z-index: 1;

  svg {
    font-size: 12px;
  }

  path {
    fill: ${({ theme }) => theme.text.secondary};
  }
`;

const NamePlaceholder = styled.div`
  text-align: left;
  @media (max-width: ${mobileBreakpoints.mobile}) {
    font-size: 12px;
    display: flex;
    justify-content: space-around;
    width: 90%;
  }
`;

const MenuButtonHandler = styled.div`
  cursor: pointer;
  font-size: 24px;
  display: none;
  margin: 0 5px;

  @media (max-width: ${mobileBreakpoints.mobile}) {
    display: ${(props) => (!props.static ? "block" : "none")};
  }
`;

export default function TopMenu(props) {
  const [showMenu, setShowMenu] = useState(false);
  const theme = useTheme();
  const access = useUserAccess();
  const hasAiAccess = useHasAiAccess() && access.dataExplorer;
  const { askAi, resumeConversation, modalNode } = useAIResultsModal();
  const dispatch = useDispatch();

  const node = useRef(null);
  useOnClickOutside(node, () => setShowMenu(false));

  const { name, avatar, ttg } = useSelector((state) => state.auth);
  const { profileImageUpload, tabs, hasDataDictionary } =
    useShallowEqualSelector((state) => ({
      profileImageUpload: state.userManagement.profileImageUpload,
      tabs: state.layout.tabs,
      hasDataDictionary: state.layout.hasDataDictionary,
    }));

  const {
    siteName,
    dataFreshness,
    activeTab,
    domain,
    themeConfig,
    userSelectedThemeName,
  } = useShallowEqualSelector((state) => ({
    siteName: state.layout.siteName,
    domain: state.layout.domain,
    activeTab: state.layout.activeTab,
    dataFreshness: state.userNotifications.dataFreshness,
    themeConfig: state.layout.theme,
    userSelectedThemeName: state.layout.userSelectedThemeName,
  }));

  const defaultThemeName = themeConfig?.name || "light";
  const currentThemeName = userSelectedThemeName || defaultThemeName;

  const dataFreshnessTable = dataFreshness?.length ? (
    <NotificationTable data={dataFreshness} />
  ) : (
    <span>Your data is up to date</span>
  );

  const handleSetShowMenu = useCallback((value) => setShowMenu(value), []);
  const mustConfigure2FA = useMustConfigure2FA();

  const handleMenuItemClick = useCallback(() => {
    setShowMenu(false);
  }, []);

  const tenantOwnerOptions = (
    <>
      {access.fileUploading && (
        <DropdownMenuItem
          {...processingsPage}
          icon="upload"
          clicked={handleMenuItemClick}
          cy="files"
        />
      )}
      {access.userManagement && (
        <DropdownMenuItem
          path="/admin/user-management/users"
          name="User Management"
          icon="users"
          clicked={handleMenuItemClick}
          cy="user-management"
        />
      )}
      {access.dataExplorer && (
        <DropdownMenuItem
          path="/admin/data-settings/data-explorer"
          name="Data Explorer"
          icon="satellite-dish"
          clicked={handleMenuItemClick}
          cy="data-explorer"
        />
      )}
      {access.dataSettings && (
        <DropdownMenuItem
          path="/admin/data-settings"
          name="Data Settings"
          icon="cog"
          clicked={handleMenuItemClick}
          cy="images"
        />
      )}
      {access.siteConfiguration && (
        <DropdownMenuItem
          path="/admin/site-configuration"
          name="Site Configuration"
          icon="sliders-h"
          clicked={handleMenuItemClick}
          cy="images"
        />
      )}
      {access.pageManagement && (
        <DropdownMenuItem
          path="/admin/page-management"
          name="Page Management"
          icon="file-alt"
          clicked={handleMenuItemClick}
          cy="images"
        />
      )}
    </>
  );

  const setScreenshotMode = (mode) => {
    dispatch(setFullPageScreenshotMode(mode));
  };

  const downloadScreenshot = (siteName, pageName) => {
    setScreenshotMode(true);
    setShowMenu(false);

    // setTimeout need to make blocks offscreen false
    setTimeout(() => {
      takeFullPageScreenshot(theme, siteName, pageName, setScreenshotMode);
    }, 500);
  };

  let title = <h3>{siteName.toUpperCase()}</h3>;
  if (props.hasLink) {
    title = <Link to={props.hasLink}>{title}</Link>;
  }

  const onTitleClick = () => {
    if (props.hasLink) {
      const link = props.hasLink.split("/").pop();
      const tab = tabs.find((tab) => tab.slug === link);
      dispatch(setActiveTab(tab));
    }
  };
  const getUserImage = () => {
    return profileImageUpload
      ? `${profileImageUpload["40x40"]}&random_number${new Date().getTime()}`
      : avatar
      ? avatar["40x40"]
      : null;
  };

  const handleThemeChange = useCallback(
    (name) => {
      dispatch(setUserSelectedThemeName(name));
    },
    [dispatch]
  );

  const renderThemeMenu = ({ treeIcon }) => (
    <ThemeMenuContainer>
      <ThemeMenuHeader>
        <span>Theme</span>
        <span>{treeIcon}</span>
      </ThemeMenuHeader>
      <ThemeMenuItem
        onClick={() => handleThemeChange(null)}
        active={!userSelectedThemeName}
      >
        Site Default
      </ThemeMenuItem>
      <ThemeMenuItem
        onClick={() => handleThemeChange("light")}
        active={currentThemeName === "light"}
      >
        Light
      </ThemeMenuItem>
      <ThemeMenuItem
        onClick={() => handleThemeChange("dark")}
        active={currentThemeName === "dark"}
      >
        Dark
      </ThemeMenuItem>
    </ThemeMenuContainer>
  );

  return (
    <Container data-cy="top-menu">
      <MenuButtonHandler onClick={props.show} static={props.static}>
        <FontAwesomeIcon icon={["fas", "bars"]} />
      </MenuButtonHandler>
      <NamePlaceholder onClick={onTitleClick}>{title}</NamePlaceholder>

      {!inIFrame && (
        <Actions static={props.static}>
          {hasAiAccess && (
            <div style={{ flex: 1, minWidth: 0, margin: "0 48px" }}>
              <AISearch
                onAskAi={askAi}
                onResumeConversation={resumeConversation}
              />
              {modalNode}
            </div>
          )}
          <ActionIconContainer hidden={mustConfigure2FA}>
            <EditorButton />
            {!hasAiAccess && (
              <div style={{ margin: itemMargin }}>
                <GlobalSearch dispatch={dispatch} />
              </div>
            )}
            <SimpleTooltip position="bottom" label={dataFreshnessTable}>
              <ActionIcon>
                <FontAwesomeIcon icon={["fas", "life-ring"]} />
              </ActionIcon>
            </SimpleTooltip>
            <SimpleTooltip position="bottom" label="You have no new messages">
              <ActionIcon>
                <FontAwesomeIcon icon={["fas", "comments"]} />
              </ActionIcon>
            </SimpleTooltip>
            <SimpleTooltip position="bottom" label={<NotificationList />} click>
              <ActionIcon>
                <FontAwesomeIcon icon={["fas", "bell"]} />
              </ActionIcon>
            </SimpleTooltip>
            <TtgFeature>
              {({ treeIcon }) => (
                <SimpleTooltip
                  position="bottom"
                  label={renderThemeMenu({ treeIcon })}
                  click
                >
                  <ActionIcon>
                    <FontAwesomeIcon icon={faAdjust} />
                  </ActionIcon>
                </SimpleTooltip>
              )}
            </TtgFeature>
          </ActionIconContainer>
          <Profile ref={node}>
            <DropdownMenu
              cy="user-menu"
              title={
                ttg ? (
                  <>
                    {name}{" "}
                    <TreeIcon title="You are a TTG user and are able to see features non-TTG users are not." />
                  </>
                ) : (
                  name
                )
              }
              background={`gray900`}
              show={showMenu}
              setShow={handleSetShowMenu}
            >
              <DropdownMenuItem
                path="/user-profile"
                name="User Profile"
                icon="id-card"
                clicked={handleMenuItemClick}
                cy="images"
              />
              {!mustConfigure2FA && (
                <DropdownMenuItem
                  name="Take Full Screenshot"
                  icon="images"
                  clicked={() =>
                    downloadScreenshot(siteName, activeTab?.displayName)
                  }
                />
              )}
              {hasDataDictionary || access.editDataDictionary ? (
                <DropdownMenuItem
                  name="Data Dictionary"
                  icon="book"
                  path="/data-dictionary"
                  clicked={handleMenuItemClick}
                />
              ) : null}
              {tenantOwnerOptions}
              <TtgFeature>
                {({ treeIcon }) => (
                  <div
                    style={{
                      display: !mustConfigure2FA ? "flex" : "none",
                      alignItems: "center",
                    }}
                  >
                    <DropdownMenuItem
                      path="/dashboard-message"
                      name="Dashboard Message"
                      icon="envelope"
                      clicked={handleMenuItemClick}
                      cy="images"
                    />
                    <span style={{ float: "right" }}>{treeIcon}</span>
                  </div>
                )}
              </TtgFeature>
              {domain === "super-duper" ? (
                <DropdownMenuItem
                  path="/admin/data-setup"
                  name="No Hands Data Wiring"
                  icon="hand-sparkles"
                  clicked={handleMenuItemClick}
                  cy="images"
                  tree
                />
              ) : null}
              <DropdownMenuItem
                path="/logout"
                name="Logout"
                icon="sign-out-alt"
                cy="link-logout"
              />
            </DropdownMenu>
            <div onClick={() => setShowMenu(!showMenu)}>
              <UserChip name={name} rounded image={getUserImage()} />
            </div>
          </Profile>
        </Actions>
      )}
    </Container>
  );
}
